// The topic filter bar on the practice hub: chips grouped as काल, विभक्ति, वचन, पुरुष, कृदन्त (and, behind a
// toggle, सन्धि and समास). Several chips can be on at once; each chip is a toggle button (aria-pressed), so Tab,
// Enter and Space work as usual. Kids see the four tenses and वचन only.
// Below the topics, for Kids and Adults alike: विकल्प रूप, a radio group of two (विकल्प रहित, the default, or विकल्प
// सहित). Native radio inputs, so the arrow keys move between the two choices; "Clear all" leaves it as it is.

import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import {
    Check,
    ChevronDown,
    ListFilter,
    X
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Bi } from '@/prakrit/components/common/bi'

import {
    TOPICS,
    TOPIC_BY_ID,
    TOPIC_GROUPS,
    VIKALP_CHOICES,
    VIKALP_TITLE
} from '@/prakrit/components/practice/practice-topics'

interface TopicFilterProps {
    /** The chosen topic ids (already reduced to what this view shows). */
    selected: string[]
    onToggle: (id: string) => void
    onClear: () => void
    kids: boolean
    /** Games that understand at least one chosen topic. */
    fitting: number
    /** विकल्प सहित: variant forms included. */
    vikalp: boolean
    onVikalp: (on: boolean) => void
}

const hiDigits = (value: number): string => String(value).replace(/[0-9]/g, d => String.fromCharCode(0x0966 + Number(d)))

const VikalpChoice: FC<{ vikalp: boolean, onVikalp: (on: boolean) => void, kids: boolean }> = ({ vikalp, onVikalp, kids }): ReactNode => (
    <div className='flex flex-col gap-1.5 border-t border-border pt-3 sm:flex-row sm:items-start sm:gap-3'>
        <p id='vikalp-filter-label' className='shrink-0 text-sm font-semibold sm:w-32 sm:pt-2.5'>
            <span lang='hi' className='hindi'>{VIKALP_TITLE.hi}</span>
            <span aria-hidden='true' className='px-1 text-muted-foreground/70'>·</span>
            <span lang='en' className='font-normal text-muted-foreground'>{VIKALP_TITLE.en}</span>
        </p>
        <div className='flex min-w-0 flex-col gap-1.5'>
            <div role='radiogroup' aria-labelledby='vikalp-filter-label' aria-describedby='vikalp-filter-note' className='flex flex-wrap gap-2'>
                {VIKALP_CHOICES.map(choice => {
                    const on = vikalp === choice.on

                    return (
                        <label
                            key={choice.on ? 'on' : 'off'}
                            className={cn(
                                'inline-flex min-h-tap cursor-pointer items-center gap-1.5 rounded-full border px-3.5 py-1 text-left transition-colors',
                                'has-[:focus-visible]:outline-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-saffron/60 has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-card',
                                on ? 'border-saffron bg-saffron/15 text-foreground' : 'border-border bg-background hover:border-saffron/60 hover:bg-saffron/5'
                            )}
                        >
                            <input
                                type='radio'
                                name='practice-vikalp'
                                value={choice.on ? '1' : '0'}
                                checked={on}
                                onChange={() => onVikalp(choice.on)}
                                className='sr-only'
                            />
                            {on && <Check className='size-4 shrink-0 text-saffron-strong' aria-hidden='true' />}
                            <span lang='hi' className={cn('hindi text-sm', on && 'font-semibold')}>{choice.label.hi}</span>
                            <span aria-hidden='true' className='text-muted-foreground/70'>·</span>
                            <span lang='en' className='text-xs text-muted-foreground'>{choice.label.en}</span>
                        </label>
                    )
                })}
            </div>
            <p id='vikalp-filter-note' className='text-xs text-muted-foreground'>
                <Bi
                    hi={kids
                        ? 'विकल्प = एक ही बात कहने का दूसरा सही रूप। "सहित" चुनो तो खेल में ये रूप भी आएँगे।'
                        : 'विकल्प रूप = एक ही खाने (पुरुष-वचन या विभक्ति-वचन) के दूसरे सही रूप, जैसे शिक्षक के नोट्स में। "सहित" चुनने पर रूपों के खेलों में लगभग आधे उत्तर विकल्प रूप होंगे; "रहित" में केवल सामान्य रूप पूछे जाते हैं।'}
                    en={kids
                        ? 'A variant is another correct way to say the same form. Choose "Include" and the games use them too.'
                        : 'Variants are the other correct forms of the same cell (person and number, or case and number), as in the teacher’s notes. Include them and about half the answers in the form games are variants; exclude them and only the standard forms are asked.'}
                    layout='inline'
                />
            </p>
        </div>
    </div>
)

export const TopicFilter: FC<TopicFilterProps> = ({ selected, onToggle, onClear, kids, fitting, vikalp, onVikalp }): ReactNode => {
    const [more, setMore] = useState(false)
    const moreChosen = selected.some(id => TOPIC_GROUPS.find(g => g.id === TOPIC_BY_ID[id]?.group)?.more)
    const showMore = more || moreChosen
    const groups = TOPIC_GROUPS.filter(g => (kids ? g.kids : !g.more || showMore))

    return (
        <section aria-labelledby='topic-filter-heading' className='flex flex-col gap-4 rounded-[1.75rem] border border-border bg-card p-4 shadow-sm sm:p-5'>
            <div className='flex flex-wrap items-start justify-between gap-3'>
                <div className='flex min-w-0 items-start gap-3'>
                    <span className={cn('flex shrink-0 items-center justify-center rounded-full bg-saffron-soft', kids ? 'size-12' : 'size-10')} aria-hidden='true'>
                        <ListFilter className={cn('text-saffron-strong', kids ? 'size-6' : 'size-5')} />
                    </span>
                    <div className='flex min-w-0 flex-col gap-0.5'>
                        <h2 id='topic-filter-heading' className='flex flex-wrap items-baseline gap-x-2'>
                            <span lang='hi' className='text-lg font-bold'>{kids ? 'क्या खेलना है?' : 'विषय चुनें'}</span>
                            <span lang='en' className='font-sans text-sm font-medium text-muted-foreground'>{kids ? 'What shall we play?' : 'Practise by topic'}</span>
                        </h2>
                        <p className='text-sm'>
                            <Bi
                                hi={kids ? 'एक या ज़्यादा चुनो; खेल उन्हीं के प्रश्न देंगे।' : 'एक या अधिक विषय चुनिए। हर खेल के स्तर उन्हीं विषयों के प्रश्न देंगे, स्तर की सामान्य सीमा से बाहर भी।'}
                                en={kids ? 'Pick one or more; the games ask about them.' : "Choose one or more topics. Each game's levels then ask about those topics, even beyond the level's usual range."}
                                enClassName='text-muted-foreground'
                            />
                        </p>
                    </div>
                </div>
                {selected.length > 0 && (
                    <button
                        type='button'
                        onClick={onClear}
                        className='inline-flex min-h-tap shrink-0 items-center gap-1.5 rounded-full border border-border bg-background px-4 text-sm font-medium hover:border-saffron/60 hover:bg-saffron/5'
                    >
                        <X className='size-4' aria-hidden='true' />
                        <Bi hi='सब हटाएँ' en='Clear all' layout='inline' always />
                    </button>
                )}
            </div>

            <div className='flex flex-col gap-3'>
                {groups.map(group => {
                    const topics = TOPICS.filter(t => t.group === group.id && (!kids || t.kids))
                    const labelId = `topic-group-${group.id}`

                    return (
                        <div key={group.id} role='group' aria-labelledby={labelId} className='flex flex-col gap-1.5 sm:flex-row sm:items-start sm:gap-3'>
                            <p id={labelId} className='shrink-0 text-sm font-semibold sm:w-32 sm:pt-2.5'>
                                <span lang='hi' className='hindi'>{group.title.hi}</span>
                                <span aria-hidden='true' className='px-1 text-muted-foreground/70'>·</span>
                                <span lang='en' className='font-normal text-muted-foreground'>{group.title.en}</span>
                            </p>
                            <ul className='flex flex-wrap gap-2'>
                                {topics.map(topic => {
                                    const on = selected.includes(topic.id)

                                    return (
                                        <li key={topic.id}>
                                            <button
                                                type='button'
                                                aria-pressed={on}
                                                onClick={() => onToggle(topic.id)}
                                                title={`${topic.full.hi} · ${topic.full.en}`}
                                                className={cn(
                                                    'inline-flex min-h-tap items-center gap-1.5 rounded-full border px-3.5 py-1 text-left transition-colors',
                                                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron/60 focus-visible:ring-offset-2 focus-visible:ring-offset-card',
                                                    on ? 'border-saffron bg-saffron/15 text-foreground' : 'border-border bg-background hover:border-saffron/60 hover:bg-saffron/5'
                                                )}
                                            >
                                                {on && <Check className='size-4 shrink-0 text-saffron-strong' aria-hidden='true' />}
                                                <span lang='hi' className={cn('hindi text-sm', on && 'font-semibold')}>{topic.label.hi}</span>
                                                <span aria-hidden='true' className='text-muted-foreground/70'>·</span>
                                                <span lang='en' className='text-xs text-muted-foreground'>{topic.label.en}</span>
                                            </button>
                                        </li>
                                    )
                                })}
                            </ul>
                        </div>
                    )
                })}
            </div>

            {!kids && !moreChosen && (
                <button
                    type='button'
                    aria-expanded={showMore}
                    onClick={() => setMore(m => !m)}
                    className='inline-flex min-h-tap items-center gap-1.5 self-start rounded-full px-2 text-sm font-medium text-saffron-strong hover:bg-saffron/5'
                >
                    <ChevronDown className={cn('size-4 transition-transform', showMore && 'rotate-180')} aria-hidden='true' />
                    {showMore
                        ? <Bi hi='सन्धि और समास के विषय छिपाएँ' en='Hide sandhi and compound topics' layout='inline' always />
                        : <Bi hi='सन्धि और समास के विषय भी' en='Sandhi and compound topics too' layout='inline' always />}
                </button>
            )}

            <p aria-live='polite' className={cn('text-sm', selected.length === 0 && 'sr-only')}>
                {selected.length > 0 && (
                    fitting > 0 ? (
                        <Bi
                            hi={`${hiDigits(fitting)} खेल इन विषयों से चलेंगे; बाक़ी खेल धुँधले दिखे हैं।`}
                            en={`${fitting} ${fitting === 1 ? 'game uses' : 'games use'} these topics; the others are dimmed.`}
                            layout='inline'
                        />
                    ) : (
                        <Bi hi='कोई खेल इन विषयों को नहीं जानता। कुछ और चुनिए।' en='No game uses these topics. Try other ones.' layout='inline' />
                    )
                )}
            </p>

            <VikalpChoice vikalp={vikalp} onVikalp={onVikalp} kids={kids} />
        </section>
    )
}
